import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {  FaEdit,  FaTrash} from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./CalendarPage.css";
import "./EventCalendar.css";
import CalendarForm from "../../components/forms/EventRegistrationForm.js";
import EventTypeForm from "../../components/forms/EventTypeRegistrationForm.js";
import { format, parseISO, isWithinInterval } from "date-fns";
import { useAcademicCalendarPDF } from "../../components/CalendarDownload";
import {
  setFilters,
  toggleModal,
  setCalendarDate,
  setPage,
  setViewMode,
  setEditing,
  setNotification,
  clearNotification,
  fetchAcademicEventTypes,
  fetchAcademicCalendarEvents,
  deleteAcademicCalendarEvents,
  editAcademicCalendarEvents,
} from "../../store/academicCalendarFormSlice.js";
import { fetchSchools } from "../../store/divisionSlice.js";
import { fetchTerms } from "../../store/termSlice";
import { fetchAllBatches } from "../../store/batchSlice";
import {
  fetchAllowedPages,
  checkRegisterationOfPages,
} from "../../store/pageAccessSlice.js";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck.js";
import AccessDenied from '../../hooks/AccessDenied'; // Adjust the import path

const AcademicCalendar = () => {
  const dispatch = useDispatch();
  const { schools } = useSelector((state) => state.schools);
  const { terms } = useSelector((state) => state.terms);
  const { batches } = useSelector((state) => state.batches);
  const { downloadPDF } = useAcademicCalendarPDF();
  const {
    formData,
    filters,
    showModal,
    calendarDate,
    allEventTypes,
    allEvents,
    currentPage,
    viewMode,
    notification,
  } = useSelector((state) => state.academicCalendar);
  const hasAccess = useSelector((state) => state.pageAccess.hasAccess);

  const eventsPerPage = 6;
  const calendarSectionRef = useRef(null);
  const timelineSectionRef = useRef(null);
  const tableSectionRef = useRef(null);

  const [manageEventModal, setManageEventModal] = useState(false);
  const [date, setDate] = useState(new Date());
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState(null);
  const [refreshEvents, setRefreshEvents] = useState(0);
  const [eventTypesNotUsed, setEventTypesNotUsed] = useState([]);
  const [openPage, setOpenPage] = useState(false);

  useTokenExpiryCheck();

  useEffect(() => {
    const storedUserData = localStorage.getItem("userInfo");
    if (storedUserData) {
      const this_Page_name = "Terms Page";
      const { roles } = JSON.parse(storedUserData);
      const isSuperAdmin = roles.includes("SuperAdmin");
      if (isSuperAdmin) {
        const checkAccess = async () => {
          const result = await dispatch(fetchAllowedPages([this_Page_name]));
          const accessPages = result?.payload?.accessPages || [];
          if (accessPages.length === 0)
            await dispatch(checkRegisterationOfPages([this_Page_name]));
        };
        checkAccess();
      }
    }
  }, [dispatch]);
  useEffect(() => {
    const storedUserData = localStorage.getItem("userInfo");

    if (storedUserData) {
      const this_Page_name = "Terms Page";
      const { roles } = JSON.parse(storedUserData);
      const isSuperAdmin = roles.includes("SuperAdmin");
      if (!isSuperAdmin) {
        const checkAccess = async () => {
          await dispatch(fetchAllowedPages([this_Page_name]));
        };
        checkAccess();
      }
    }
  }, [dispatch]);
  useEffect(() => {
    if (hasAccess) {
      setOpenPage(true);
    }
  }, [hasAccess]);

  // Fetch initial data
  useEffect(() => {
    dispatch(fetchSchools());
    dispatch(fetchTerms());
    dispatch(fetchAllBatches());
  }, [dispatch]);

  useEffect(() => {
    if (allEvents) {
      setFilteredEvents(allEvents);
      if (allEventTypes) {
        const unUsedEventTypes = allEventTypes.filter(
          (eventType) =>
            !allEvents.some(
              (event) => event.eventTypeID === eventType.eventTypeID
            )
        );
        setEventTypesNotUsed(unUsedEventTypes);
      }
    }
  }, [allEvents, allEventTypes]);

  useEffect(() => {
    dispatch(fetchAcademicEventTypes());
    dispatch(fetchAcademicCalendarEvents());
  }, [dispatch]);

  useEffect(() => {
    if (refreshEvents > 0) dispatch(fetchAcademicCalendarEvents());
  }, [dispatch, refreshEvents]);

  // Notification timeout
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => dispatch(clearNotification()), 3000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, notification]);

  // Filter handlers
  const handleFilterChange = useCallback(
    ({ target: { name, value } }) => {
      dispatch(
        setFilters(
          name === "schoolId"
            ? { [name]: value, batchId: "All" }
            : { [name]: value }
        )
      );
    },
    [dispatch]
  );

  const filterEvents = useCallback((filters = {}, events = []) => {
    const {
      schoolId = "All",
      batchId = "All",
      termId = "",
      eventType = "All",
    } = filters;
    const safeEvents = Array.isArray(events) ? events : [];
    return safeEvents
      .filter((event) => {
        const matchesTerm =
          !termId || event.termID === null || event.termID === termId;
        const matchesEventType =
          eventType === "All" || event.eventTypeID === eventType;

        const matchesSchool =
          schoolId === "All" || event.schoolID === null
            ? true
            : event.schoolID === schoolId;

        const matchesBatch =
          batchId === "All" || event.batchID === null
            ? true
            : event.batchID === batchId;

        return matchesSchool && matchesBatch && matchesTerm && matchesEventType;
      })
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  }, []);

  const filtered_Events = useMemo(
    () => filterEvents(filters, allEvents),
    [filters, allEvents, filterEvents]
  );

  const totalPages = Math.ceil(filtered_Events.length / eventsPerPage);
  const paginatedEvents = useMemo(
    () =>
      filtered_Events.slice(
        (currentPage - 1) * eventsPerPage,
        currentPage * eventsPerPage
      ),
    [filtered_Events, currentPage, eventsPerPage]
  );

  const filteredBatches = useMemo(
    () =>
      filters?.schoolId === "All"
        ? batches
        : batches.filter((b) => b?.schoolId === filters?.schoolId),
    [filters?.schoolId, batches]
  );

  // Event handlers
  const handleDownloadPDF = useCallback(() => {
    downloadPDF(
      filteredEvents,
      terms.find((t) => t.termId === filters.term)?.academicYear || "2024/2025",
      (message) => dispatch(setNotification(message)),
      allEventTypes
    );
  }, [
    filteredEvents,
    dispatch,
    filters.term,
    formData,
    allEventTypes,
    schools,
    batches,
    terms,
  ]);

  const handleEdit = async (event) => {
    console.log("terms", terms);
    console.log("event", event);
    setSelectedEvents(event);
    dispatch(setEditing(event));
    const eventId = event.eventID;
    console.log("eventId", eventId);
    dispatch(toggleModal(true));
    //dispatch(editAcademicCalendarEvents({ eventId, event }));
    //dispatch(setNotification("Event updated successfully!"));
  };

  const handleDeleteEvent = async (eventID) => {
    console.log("eventTypeID: -------------", eventID);
    dispatch(deleteAcademicCalendarEvents(eventID));
    dispatch(setNotification("Event deleted successfully!"));
    setRefreshEvents(refreshEvents + 1);
  };

  const handlePageChange = useCallback(
    (dir) => {
      const nextPage = currentPage + dir;
      if (nextPage >= 1 && nextPage <= totalPages) dispatch(setPage(nextPage));
    },
    [dispatch, currentPage, totalPages]
  );

  // Calendar specific functions
  const getEventsForDate = useCallback(
    (date) => {
      return allEvents?.filter((event) => {
        try {
          const startDate = parseISO(event.eventStartDate);
          const endDate = parseISO(event.eventEndDate);
          return isWithinInterval(date, { start: startDate, end: endDate });
        } catch (error) {
          console.error("Error parsing event dates:", error);
          return false;
        }
      });
    },
    [allEvents]
  );

  const handleDateClick = useCallback(
    (clickedDate) => {
      const eventsForDate = getEventsForDate(clickedDate);
      setFilteredEvents(eventsForDate);
      setDate(clickedDate);
    },
    [getEventsForDate]
  );

  const tileContent = useCallback(
    ({ date, view }) => {
      if (view !== "month") return null;
      const eventsForDate = getEventsForDate(date);
      if (eventsForDate.length === 0) return null;

      return (
        <div className="event-indicators">
          {eventsForDate.slice(0, 3).map((event, index) => (
            <div
              key={index}
              className={`event-indicator ${event.eventTypeName
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              title={event.eventTypeName}
            />
          ))}
          {eventsForDate.length > 3 && (
            <div className="event-indicator more-events">
              +{eventsForDate.length - 3}
            </div>
          )}
        </div>
      );
    },
    [getEventsForDate]
  );

  const tileClassName = useCallback(
    ({ date, view }) => {
      if (view !== "month") return "";
      const eventsForDate = getEventsForDate(date);
      if (eventsForDate.length === 0) return "";

      const classes = ["has-events"];
      eventsForDate.forEach((event) => {
        classes.push(
          `event-type-${event.eventTypeName.toLowerCase().replace(/\s+/g, "-")}`
        );
      });
      return classes.join(" ");
    },
    [getEventsForDate]
  );

  const renderTimeline = useCallback(() => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div className="timeline-section" ref={timelineSectionRef}>
        <h3>Timeline View</h3>
        {months.map((month, i) => {
          const monthEvents = filteredEvents.filter(
            (e) => new Date(e.eventStartDate).getMonth() === i
          );

          return (
            <div key={month} className="timeline-month">
              <h4>{month}</h4>
              {monthEvents.length ? (
                <ul>
                  {monthEvents.map((e) => (
                    <li
                      key={e.eventID}
                      className={`timeline-event ${allEventTypes
                        .find((et) => et.eventTypeID === e.eventTypeID)
                        ?.eventName.toLowerCase()
                        .replace(/ /g, "-")}-event`}
                    >
                      <div className="event-title">{e.eventTypeName}</div>
                      <div className="event-details">
                        <span>
                          📅 {format(parseISO(e.eventStartDate), "MMM d")}{" "}
                          {e.eventEndDate &&
                            `→ ${format(
                              parseISO(e.eventEndDate),
                              "MMM d, yyyy"
                            )}`}
                        </span>
                        <span>📍 {e.schoolName || "All"}</span>
                        <span>📚 {e.batchName || "All"}</span>
                        <span>🕰️ {e.termName || "All"}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No events in {month}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [filteredEvents, allEventTypes]);

  if (!allEvents) return <div>Loading Calendar...</div>;

  return (
    <div className="calendar-form-container" style={{ paddingTop: "20px" }}>
      {notification && (
        <div className="notification" role="alert" aria-live="polite">
          {notification}
        </div>
      )}

      <div className="header-section">
        <h2>School Calendar</h2>
        <div>
          <button
            className="download-btn"
            onClick={handleDownloadPDF}
            aria-label="Download calendar as PDF"
          >
            Download Acc. Calendar (PDF)
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-row">
          <div className="form-group">
            <label>School</label>
            <select
              name="schoolId"
              value={filters?.schoolId || "All"}
              onChange={handleFilterChange}
              aria-label="Filter by school"
            >
              <option value="All">All</option>
              {schools.map((s) => (
                <option key={s?.schoolId} value={s?.schoolId}>
                  {s.schoolName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Batch</label>
            <select
              name="batchId"
              value={filters?.batchId || "All"}
              onChange={handleFilterChange}
              aria-label="Filter by batch"
            >
              <option value="All">All</option>
              {Array.isArray(filteredBatches) &&
                filteredBatches?.map((b) => (
                  <option key={b.batchId} value={b.batchId}>
                    {b.batchName}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-group">
            <label>Term</label>
            <select
              name="termId"
              value={filters?.termId || ""}
              onChange={handleFilterChange}
              aria-label="Filter by term"
            >
              <option value="">Select Term</option>
              {terms.map((t) => (
                <option key={t.termId} value={t.termId}>
                  {`${t.name} (${t.academicYear})`}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="filter-row-single">
          <div className="form-group">
            <label>Event Type</label>
            <select
              name="eventType"
              value={filters?.eventType || "All"}
              onChange={handleFilterChange}
              aria-label="Filter by event type"
            >
              <option value="All">All</option>
              {allEventTypes.map((et) => (
                <option key={et.eventTypeID} value={et.eventTypeID}>
                  {et.eventName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="table-section" ref={tableSectionRef}>
        <div
          className="table-wrapper"
          style={{ maxHeight: "400px", overflowY: "auto" }}
        >
          <table className="calendar-table">
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th>Event Type</th>
                <th>Event Descreption</th>
                <th>School</th>
                <th>Dates</th>
                <th>Term</th>
                <th>Batch</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEvents?.length ? (
                paginatedEvents.map((e) => (
                  <tr key={e.eventID}>
                    <td>{e.eventTypeName}</td>
                    <td>{e.eventDescription}</td>
                    <td>{e.schoolName || "All"}</td>
                    <td>
                      {format(parseISO(e.eventStartDate), "MMM d, yyyy")}
                      {e.eventEndDate &&
                        ` → ${format(parseISO(e.eventEndDate), "MMM d, yyyy")}`}
                    </td>
                    <td>{e.termName || "-"}</td>

                    <td>{e.batchName || "All"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">No events found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="table-footer">
          <button
            className="nav-btn"
            onClick={() => handlePageChange(-1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            Prev
          </button>
          <button
            className="nav-btn"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            Next
          </button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
        </div>
      </div>

      <div className="view-toggle">
        <button
          className={`toggle-btn ${viewMode === "calendar" ? "active" : ""}`}
          onClick={() => dispatch(setViewMode("calendar"))}
          aria-label="Switch to calendar view"
        >
          Calendar View
        </button>
        <button
          className={`toggle-btn ${viewMode === "timeline" ? "active" : ""}`}
          onClick={() => dispatch(setViewMode("timeline"))}
          aria-label="Switch to timeline view"
        >
          Timeline View
        </button>
      </div>

      {viewMode === "calendar" ? (
        <div className="calendar-section" ref={calendarSectionRef}>
          <h3>Academic Calendar</h3>
          <div className="event-calendar">
            <Calendar
              onChange={handleDateClick}
              value={date}
              view="month"
              tileContent={tileContent}
              tileClassName={tileClassName}
              onClickDay={handleDateClick}
            />

            {selectedEvents?.length > 0 && (
              <div className="event-details-panel">
                <h3>Events on {format(date, "MMMM d, yyyy")}</h3>
                <div className="events-list">
                  {selectedEvents.map((event, index) => (
                    <div key={index} className="event-item">
                      <div
                        className={`event-type-badge ${event.eventTypeName
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {event.eventTypeName}
                      </div>
                      <div className="event-info">
                        <h4>{event.eventTypeName}</h4>
                        <p>
                          <strong>School:</strong> {event.schoolName}
                        </p>
                        <p>
                          <strong>Batch:</strong> {event.batchName}
                        </p>
                        <p>
                          <strong>Term:</strong> {event.termName}
                        </p>
                        <p>
                          <strong>Academic Year:</strong> {event.academicYear}
                        </p>
                        <p>
                          <strong>Date Range:</strong>{" "}
                          {format(parseISO(event.eventStartDate), "MMM d")} -{" "}
                          {format(parseISO(event.eventEndDate), "MMM d, yyyy")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        renderTimeline()
      )}

      {showModal && (
        <CalendarForm
          schools={schools}
          batches={batches}
          terms={terms}
          eventTypes={allEventTypes}
          tableSectionRef={tableSectionRef}
        />
      )}

      {manageEventModal && (
        <EventTypeForm onClose={() => setManageEventModal(false)} />
      )}
    </div>
  );
};

export default AcademicCalendar;
